import { useState } from "react";
import { AttachmentCount, useActionAttachmentCount } from "@/components/shared/AttachmentCount";
import { ParentChip } from "@/lib/parents";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { Avatar, AssigneeAvatars } from "@/components/shared/UI";
import { StatusPill, PriorityIcon, OverduePill, TypePill } from "@/components/shared/Pills";
import { DeleteButton } from "@/components/shared/DeleteButton";
import { formatDate, formatDateShort, dueLabel, recurLabel } from "@/lib/format";
import { sortSubtasks } from "@/lib/taskSort";
import { cn } from "@/lib/utils";
import {
  ListChecks, CheckCircle2, Trash2, Copy, Building2, Hotel, User, UserPlus, Repeat, CalendarDays, Check, X, ChevronRight, ChevronDown,
} from "lucide-react";

function TaskAttachCount({ id }) {
  return <AttachmentCount count={useActionAttachmentCount(id)} testid={`task-attach-count-${id}`} />;
}

function TaskMeta({ task }) {
  const done = task.status === "Completed" || task.status === "Cancelled";
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400">
      {!done && <PriorityIcon priority={task.priority} />}
      {task.activity_type && task.activity_type !== "Task" && <TypePill type={task.activity_type} />}
      <StatusPill status={task.status} />
      <TaskAttachCount id={task.id} />
      {task.sub_types?.length > 0 && <span className="text-slate-500" data-testid={`task-subtypes-${task.id}`}>{task.sub_types.join(", ")}</span>}
      {task.is_overdue && !done ? <OverduePill /> : task.due_date && (
        <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{task.due_start ? `${formatDateShort(task.due_start)} – ${formatDateShort(task.due_date)}` : (done ? formatDate(task.due_date) : dueLabel(task.due_date))}</span>
      )}
      {task.recurring && task.recurring !== "None" && <span className="flex items-center gap-1 text-sky-500" data-testid={`task-recur-indicator-${task.id}`}><Repeat className="h-3 w-3" />{recurLabel(task.recurring, task.due_date)}</span>}
      {task.organisation_name && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{task.organisation_name}</span>}
      {task.property_name && <span className="flex items-center gap-1"><Hotel className="h-3 w-3" />{task.property_name}</span>}
      {task.contact_name && <span className="flex items-center gap-1"><User className="h-3 w-3" />{task.contact_name}</span>}
      <ParentChip task={task} />
      {task.assignor_note && <span className="flex items-center gap-1 text-sky-500"><UserPlus className="h-3 w-3" />by {task.created_by_name}</span>}
    </div>
  );
}

function fmtSubDone(iso, name) {
  if (!iso && !name) return "";
  let when = "";
  if (iso) { const d = new Date(iso); if (!isNaN(d)) when = d.toLocaleDateString(undefined, { day: "numeric", month: "short" }) + " " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); }
  return `${name || "Done"}${when ? ` · ${when}` : ""}`;
}

function CardChecklist({ task, onToggleSubtask }) {
  const subs = task.subtasks || [];
  if (!subs.length) return null;
  return (
    <div className="mt-2 space-y-2 border-t border-slate-100 pt-2" onClick={(e) => e.stopPropagation()} data-testid={`task-checklist-${task.id}`}>
      {sortSubtasks(subs).map((s) => (
        <div key={s.id} className="flex items-start gap-1.5">
          <button onClick={() => onToggleSubtask(task, s.id)} data-testid={`card-subtask-toggle-${s.id}`}
            className={cn("mt-[3px] flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border transition-colors",
              s.completed ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 text-transparent hover:border-emerald-400")}>
            <Check className="h-2.5 w-2.5" />
          </button>
          <div className="min-w-0 flex-1 pt-px leading-[16px]">
            <span className={cn("text-[12px] leading-[16px]", s.completed ? "text-slate-400 line-through" : "text-slate-600")}>{s.title}</span>
            {s.completed && <span className="ml-1 text-[10px] text-emerald-600">· {fmtSubDone(s.completed_at, s.completed_by_name)}</span>}
          </div>
          {s.assigned_to_name && <Avatar name={s.assigned_to_name} user className="h-4 w-4 shrink-0 text-[8px]" />}
        </div>
      ))}
    </div>
  );
}

function TaskCardInner({ task, onEdit, onComplete, onRefresh, onToggleSubtask, onDuplicate, dragRef, dragProps, isDragging }) {
  const style = dragProps?.style;
  const done = task.status === "Completed" || task.status === "Cancelled";
  const [subsOpen, setSubsOpen] = useState(false);
  const hasSubs = (task.subtask_total || 0) > 0;
  return (
    <div
      ref={dragRef}
      style={style}
      {...(dragProps?.attributes || {})}
      {...(dragProps?.listeners || {})}
      onClick={() => onEdit(task)}
      data-testid={`task-card-${task.id}`}
      className={cn("group cursor-pointer rounded-lg border border-slate-200 bg-white p-3 shadow-sm transition-shadow hover:shadow-md", isDragging && "opacity-60 shadow-lg")}
    >
      <div className="relative flex items-start gap-2">
        {task.status === "Cancelled" ? (
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-rose-300 text-rose-500" data-testid={`task-cancelled-${task.id}`}>
            <X className="h-3 w-3" />
          </span>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); if (!done) onComplete(task); }}
            data-testid={`task-complete-${task.id}`}
            className={cn("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-all",
              done ? "border-emerald-500 text-emerald-500" : "border-slate-300 text-transparent hover:border-emerald-500 hover:text-emerald-500")}
          >
            <CheckCircle2 className="h-4 w-4" />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <p className={cn("text-sm font-medium text-slate-800", done && "text-slate-400 line-through")}>{task.title}</p>
          <div className="mt-2 flex items-center gap-2">
            <div className="min-w-0 flex-1"><TaskMeta task={{ ...task, assignor_note: task.created_by_id && !(task.assigned_to_ids || []).includes(task.created_by_id) }} /></div>
            <AssigneeAvatars names={task.assigned_to_names} className="hidden shrink-0 sm:flex" />
          </div>
          {hasSubs && (
            <button
              onClick={(e) => { e.stopPropagation(); setSubsOpen((v) => !v); }}
              data-testid={`task-subtask-toggle-${task.id}`}
              className="mt-1.5 flex items-center gap-1 text-xs text-slate-400 transition-colors hover:text-slate-600"
            >
              {subsOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              <ListChecks className="h-3 w-3" />
              <span data-testid={`task-subtask-count-${task.id}`}>{task.subtask_done}/{task.subtask_total}</span>
              <span>{subsOpen ? "Hide" : "Show"} sub-tasks</span>
            </button>
          )}
          {hasSubs && subsOpen && <CardChecklist task={task} onToggleSubtask={onToggleSubtask} />}
        </div>
        <div className="absolute right-1 top-1 z-10 flex items-center gap-0.5 rounded-md bg-white/95 opacity-0 shadow-sm ring-1 ring-slate-100 transition-opacity group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => onDuplicate(task)} title="Duplicate task" className="rounded-md p-1 text-slate-400 hover:bg-sky-50 hover:text-sky-600" data-testid={`task-duplicate-${task.id}`}><Copy className="h-3.5 w-3.5" /></button>
          <DeleteButton
            endpoint="/actions" id={task.id} label="task" onDeleted={onRefresh} recurringSeries={Boolean(task.series_id)}
            trigger={<button className="rounded-md p-1 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`task-delete-${task.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
          />
        </div>
      </div>
    </div>
  );
}

function DraggableTaskCard({ task, ...cardProps }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: task.id });
  const { setNodeRef: setDropRef } = useDroppable({ id: `card:${task.id}` });
  const ref = (node) => { setNodeRef(node); setDropRef(node); };
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50 } : undefined;
  return <TaskCardInner task={task} {...cardProps} dragRef={ref} dragProps={{ attributes, listeners, style }} isDragging={isDragging} />;
}

export function TaskCard({ draggable = true, ...props }) {
  if (draggable) return <DraggableTaskCard {...props} />;
  return <TaskCardInner {...props} />;
}
